import { useEffect, useLayoutEffect, useMemo, useState } from 'react'

import { useArabicSessionLanguage } from '@context/SessionSettingsContext'

import { TooltipDirection } from '@ui/common/layout.types'

const EDGE_PADDING = 0
const TOP_BAR_GAP = 8
const OFFSET_TOP_VARIANT = 0.15
const OFFSET_BOTTOM_VARIANT = 0.85
const SCROLL_LISTENER_OPTIONS: AddEventListenerOptions = { passive: true, capture: true }

interface UseTooltipPositionParams {
  boxRef: React.RefObject<HTMLDivElement | null>
  contentDirection: TooltipDirection
  isOpen: boolean
  triggerRef: React.RefObject<HTMLDivElement | null>
  distance?: string
}

interface UseTooltipPosition {
  contentOrigin: Positions | null
  direction: TooltipDirection
}

export type Positions = {
  top: number
  left: number
  bottom?: number
  transform?: string
}

export type Rect = {
  top: number
  left: number
  width: number
  height: number
  bottom: number
  right: number
}

export function useTooltipPosition({
  boxRef,
  contentDirection,
  isOpen,
  triggerRef,
  distance = '1.6rem',
}: UseTooltipPositionParams): UseTooltipPosition {
  const [origin, setOrigin] = useState<Positions | null>(null)
  const [placement, setPlacement] = useState<TooltipDirection>(contentDirection)

  const isArabic = useArabicSessionLanguage()

  useLayoutEffect(() => {
    if (!isOpen || !triggerRef.current || !boxRef.current) {
      setOrigin(null)
      setPlacement(contentDirection)
      return
    }

    const tipEl = boxRef.current
    const rect = triggerRef.current.getBoundingClientRect()

    const { top, left, bestPlacement } = calculateBestPosition({
      tipEl,
      rect,
      preferred: contentDirection,
      distance,
      isArabic,
    })

    setOrigin({ top: Math.round(top), left: Math.round(left), transform: 'none' })
    setPlacement(bestPlacement)
  }, [isOpen, contentDirection, triggerRef, boxRef, distance, isArabic])

  useEffect(() => {
    if (!isOpen || !triggerRef.current) return

    const handle = () => {
      if (!triggerRef.current || !boxRef.current) return
      const rect = triggerRef.current.getBoundingClientRect()
      const { top, left, bestPlacement } = calculateBestPosition({
        tipEl: boxRef.current,
        rect,
        preferred: contentDirection,
        distance,
        isArabic,
      })
      setOrigin({ top: Math.round(top), left: Math.round(left), transform: 'none' })
      setPlacement(bestPlacement)
    }

    window.addEventListener('resize', handle)
    window.addEventListener('scroll', handle, SCROLL_LISTENER_OPTIONS)

    const ro = new ResizeObserver(handle)
    ro.observe(triggerRef.current)
    if (boxRef.current) ro.observe(boxRef.current)

    return () => {
      window.removeEventListener('resize', handle)
      window.removeEventListener('scroll', handle, SCROLL_LISTENER_OPTIONS)
      ro.disconnect()
    }
  }, [isOpen, triggerRef, boxRef, contentDirection, distance, isArabic])

  const contentOrigin = useMemo<Positions | null>(() => {
    if (!isOpen || !origin) return null
    return origin
  }, [isOpen, origin])

  return { contentOrigin, direction: placement }
}

function calculateBestPosition({
  tipEl,
  rect,
  preferred,
  distance,
  isArabic,
}: {
  tipEl: HTMLElement
  rect: DOMRect
  preferred: TooltipDirection
  distance: string
  isArabic: boolean
}) {
  const prev = prepareElementForMeasurement(tipEl)
  const viewportWidth = window.innerWidth
  const viewportHeight = window.innerHeight
  const tooltipWidth = tipEl.offsetWidth
  const tooltipHeight = tipEl.offsetHeight
  const topBoundary = getTopBoundary()

  const candidates = getCandidates(preferred)

  type Cand = { direction: TooltipDirection; top: number; left: number; overflow: number }

  const candList: Cand[] = candidates.map((direction) => {
    const pos = getPosition(direction, rect, tooltipWidth, tooltipHeight, distance, isArabic)
    const overflowLeft = Math.max(0, EDGE_PADDING - pos.left)
    const overflowTop = Math.max(0, topBoundary - pos.top)
    const overflowRight = Math.max(0, pos.left + tooltipWidth - (viewportWidth - EDGE_PADDING))
    const overflowBottom = Math.max(0, pos.top + tooltipHeight - (viewportHeight - EDGE_PADDING))
    return {
      direction,
      top: pos.top,
      left: pos.left,
      overflow: overflowLeft + overflowTop + overflowRight + overflowBottom,
    }
  })

  const fitting = candList.find((c) => c.overflow === 0)
  const best = fitting ?? candList.sort((a, b) => a.overflow - b.overflow)[0]

  const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v))
  const top = clamp(best.top, topBoundary, viewportHeight - EDGE_PADDING - tooltipHeight)
  const left = clamp(best.left, EDGE_PADDING, viewportWidth - EDGE_PADDING - tooltipWidth)

  restoreElementStyles(tipEl, prev)

  return { top, left, bestPlacement: best.direction }
}

function getCandidates(direction: TooltipDirection): TooltipDirection[] {
  const map: Record<TooltipDirection, TooltipDirection[]> = {
    top: ['top', 'bottom'],
    bottom: ['bottom', 'top'],
    left: ['left', 'right'],
    right: ['right', 'left'],
    topLeft: ['topLeft', 'bottomLeft', 'topRight'],
    topRight: ['topRight', 'bottomRight', 'topLeft'],
    bottomLeft: ['bottomLeft', 'topLeft', 'bottomRight'],
    bottomRight: ['bottomRight', 'topRight', 'bottomLeft'],
    leftTop: ['leftTop', 'rightTop', 'leftBottom'],
    leftBottom: ['leftBottom', 'rightBottom', 'leftTop'],
    rightTop: ['rightTop', 'leftTop', 'rightBottom'],
    rightBottom: ['rightBottom', 'leftBottom', 'rightTop'],
  }

  const list = map[direction] ?? [direction]
  const seen = new Set<TooltipDirection>()
  return list.filter((d) => (seen.has(d) ? false : (seen.add(d), true)))
}

function getPosition(
  direction: TooltipDirection,
  rect: DOMRect,
  tw: number,
  th: number,
  distance: string,
  isArabic: boolean
) {
  const dist = parseDistance(distance)
  const centerX = rect.left + rect.width / 2
  const centerY = rect.top + rect.height / 2

  const positions: Record<TooltipDirection, { top: number; left: number }> = {
    top: { top: rect.bottom + dist, left: centerX - tw / 2 },
    bottom: { top: rect.top - dist - th, left: centerX - tw / 2 },
    left: { top: centerY - th / 2, left: rect.right + dist },
    leftTop: { top: rect.top - OFFSET_TOP_VARIANT * th, left: rect.right + dist },
    leftBottom: { top: rect.bottom - OFFSET_BOTTOM_VARIANT * th, left: rect.right + dist },
    right: { top: centerY - th / 2, left: rect.left - tw - dist },
    rightTop: { top: rect.top - OFFSET_TOP_VARIANT * th, left: rect.left - tw - dist },
    rightBottom: { top: rect.bottom - OFFSET_BOTTOM_VARIANT * th, left: rect.left - tw - dist },
    topRight: { top: rect.bottom + dist, left: rect.right - tw },
    topLeft: {
      top: rect.bottom + dist,
      left: isArabic ? rect.right - tw : rect.left,
    },
    bottomRight: {
      top: rect.top - th - dist,
      left: rect.right - tw,
    },
    bottomLeft: {
      top: rect.top - th - dist,
      left: rect.left,
    },
  }

  return positions[direction] ?? { top: rect.bottom + dist, left: centerX - tw / 2 }
}

function parseDistance(distance: string): number {
  if (!distance) return 0
  const val = Number.parseFloat(distance)
  if (Number.isNaN(val)) return 0
  if (distance.includes('rem')) {
    const root = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16
    return val * root
  }
  return val
}

function prepareElementForMeasurement(el: HTMLElement) {
  const prev = {
    visibility: el.style.visibility,
    transform: el.style.transform,
    left: el.style.left,
    top: el.style.top,
    position: el.style.position,
  }
  el.style.visibility = 'hidden'
  el.style.transform = 'none'
  el.style.left = '0px'
  el.style.top = '0px'
  el.style.position = 'fixed'
  return prev
}

function restoreElementStyles(
  el: HTMLElement,
  prev: ReturnType<typeof prepareElementForMeasurement>
) {
  el.style.visibility = prev.visibility
  el.style.transform = prev.transform
  el.style.left = prev.left
  el.style.top = prev.top
  el.style.position = prev.position
}

function getTopBoundary(): number {
  const style = getComputedStyle(document.documentElement)

  const topBarHeight = parseFloat(style.getPropertyValue('--topbar-height'))
  const systemNotificationsHeight = parseFloat(
    style.getPropertyValue('--system-notifications-height')
  )

  const total =
    (Number.isNaN(topBarHeight) ? 0 : topBarHeight) +
    (Number.isNaN(systemNotificationsHeight) ? 0 : systemNotificationsHeight)

  return total + TOP_BAR_GAP
}
